import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { resolveApiErrorMessage } from '@/api/resolveApiErrorMessage';
import type { PatientOptionItem } from '@/api/patientOptions/patientOptionsApi';
import { QueryErrorState } from '@/components/admin/shared/QueryErrorState';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Icon } from '@/components/common/Icon';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import {
  useRemovePatientOptionImageMutation,
  useSetWorkspacePatientOptionsMutation,
  useUploadPatientOptionImageMutation,
  useWorkspacePatientOptionsQuery,
} from '@/hooks/useWorkspacePatientOptions';
import { cn } from '@/lib/utils';
import { isBlank } from '@/utils/formValidation';
import { WORKSPACE_PATIENT_OPTIONS_QUERY_KEY } from '@/constants/queryKeys';
import { showErrorNotification, showSuccessNotification } from '@/utils/notifications';
import { FieldError } from '@/components/ui/fieldError';
import { EntityImageUpload } from '@/components/practitioner/TreatmentLibrary/EntityImageUpload';

export function PatientOptionsSection() {
  const { t } = useTranslation('patients');
  const optionsQuery = useWorkspacePatientOptionsQuery();

  if (optionsQuery.isLoading === true) {
    return (
      <div className="py-16 flex justify-center">
        <LoadingSpinner className="w-7 h-7" />
      </div>
    );
  }

  if (optionsQuery.isError === true || optionsQuery.data === undefined) {
    return (
      <QueryErrorState
        onRetry={function retry() {
          optionsQuery.refetch();
        }}
      />
    );
  }

  return (
    <div className="space-y-5">
      <OptionEditor
        category="species"
        title={t('settings.species')}
        initialItems={optionsQuery.data.species}
        withImages
      />
      <OptionEditor
        category="warning"
        title={t('settings.warnings')}
        initialItems={optionsQuery.data.warnings}
        withImages={false}
      />
    </div>
  );
}

interface EditableRow {
  code: string | null;
  labelEn: string;
  labelPl: string;
  imageUrl: string | null;
  pendingImageFile: File | null;
  pendingImageRemoved: boolean;
}

interface OptionEditorProps {
  category: 'species' | 'warning';
  title: string;
  initialItems: PatientOptionItem[];
  withImages: boolean;
}

function OptionEditor({ category, title, initialItems, withImages }: OptionEditorProps) {
  const { t } = useTranslation('patients');
  const queryClient = useQueryClient();
  const mutation = useSetWorkspacePatientOptionsMutation();
  const uploadImage = useUploadPatientOptionImageMutation();
  const removeImage = useRemovePatientOptionImageMutation();
  const [rows, setRows] = useState<EditableRow[]>(toRows(initialItems));
  const [isSaving, setIsSaving] = useState(false);
  const [invalidRowIndexes, setInvalidRowIndexes] = useState<number[]>([]);

  useEffect(
    function syncFromServer() {
      setRows(toRows(initialItems));
      setInvalidRowIndexes([]);
    },
    [initialItems]
  );

  function selectImage(index: number, file: File) {
    updateRow(index, {
      imageUrl: URL.createObjectURL(file),
      pendingImageFile: file,
      pendingImageRemoved: false,
    });
  }

  function clearImage(index: number) {
    updateRow(index, { imageUrl: null, pendingImageFile: null, pendingImageRemoved: true });
  }

  function updateRow(index: number, patch: Partial<EditableRow>) {
    setInvalidRowIndexes([]);
    setRows(function apply(previous) {
      return previous.map(function mapRow(row, rowIndex) {
        return rowIndex === index ? { ...row, ...patch } : row;
      });
    });
  }

  function removeRow(index: number) {
    setRows(function apply(previous) {
      return previous.filter(function keep(_row, rowIndex) {
        return rowIndex !== index;
      });
    });
  }

  function addRow() {
    setRows(function apply(previous) {
      return [
        ...previous,
        {
          code: null,
          labelEn: '',
          labelPl: '',
          imageUrl: null,
          pendingImageFile: null,
          pendingImageRemoved: false,
        },
      ];
    });
  }

  async function save() {
    const incompleteIndexes = rows.reduce<number[]>(function collect(indexes, row, rowIndex) {
      if (isBlank(row.labelEn) || isBlank(row.labelPl)) {
        indexes.push(rowIndex);
      }
      return indexes;
    }, []);
    if (incompleteIndexes.length > 0) {
      setInvalidRowIndexes(incompleteIndexes);
      showErrorNotification(t('settings.incompleteRow'));
      return;
    }

    const items = rows.map(function toDraft(row) {
      return { code: row.code, labelEn: row.labelEn.trim(), labelPl: row.labelPl.trim() };
    });
    setIsSaving(true);
    try {
      const options = await mutation.mutateAsync({ category, items });
      const savedList = category === 'species' ? options.species : options.warnings;
      let didChangeImage = false;
      for (let position = 0; position < rows.length; position += 1) {
        const row = rows[position];
        const savedItem = savedList[position];
        if (row === undefined || savedItem === undefined) {
          continue;
        }
        if (row.pendingImageFile !== null) {
          await uploadImage.mutateAsync({ category, code: savedItem.code, file: row.pendingImageFile });
          didChangeImage = true;
        } else if (row.pendingImageRemoved && row.code !== null) {
          await removeImage.mutateAsync({ category, code: savedItem.code });
          didChangeImage = true;
        }
      }
      if (didChangeImage) {
        await queryClient.invalidateQueries({ queryKey: WORKSPACE_PATIENT_OPTIONS_QUERY_KEY });
      }
      setRows(toRows(savedList));
      setInvalidRowIndexes([]);
      showSuccessNotification(t('settings.saved'));
    } catch (error) {
      const message = resolveApiErrorMessage(error);
      showErrorNotification(message.length > 0 ? message : t('settings.saveError'));
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <Card padding="md">
      <h2 className="text-sm font-bold text-zooindigo mb-4">{title}</h2>

      <div
        className={cn(
          'hidden sm:grid gap-2 mb-1.5 px-1',
          withImages ? 'grid-cols-[auto_1fr_1fr_auto]' : 'grid-cols-[1fr_1fr_auto]'
        )}
      >
        {withImages ? (
          <span className="w-11 text-[10px] font-bold uppercase tracking-wider text-zoomuted">
            {t('settings.image')}
          </span>
        ) : null}
        <span className="text-[10px] font-bold uppercase tracking-wider text-zoomuted">
          {t('settings.labelEn')}
        </span>
        <span className="text-[10px] font-bold uppercase tracking-wider text-zoomuted">
          {t('settings.labelPl')}
        </span>
        <span className="w-8" />
      </div>

      {rows.length === 0 ? (
        <p className="text-sm text-zoomuted mb-3">{t('settings.empty')}</p>
      ) : (
        <div className="space-y-2.5 mb-3">
          {rows.map(function renderRow(row, index) {
            return (
              <div
                key={index}
                className={cn(
                  'flex flex-col sm:grid gap-2.5 sm:gap-2 sm:items-center border-b border-zooborder pb-3 last:border-b-0 last:pb-0 sm:border-b-0 sm:pb-0',
                  withImages ? 'sm:grid-cols-[auto_1fr_1fr_auto]' : 'sm:grid-cols-[1fr_1fr_auto]'
                )}
              >
                {withImages ? (
                  <EntityImageUpload
                    compact
                    imageUrl={row.imageUrl}
                    isBusy={isSaving}
                    onSelect={function pick(file) {
                      selectImage(index, file);
                    }}
                    onRemove={function drop() {
                      clearImage(index);
                    }}
                  />
                ) : null}
                <Input
                  value={row.labelEn}
                  hasError={invalidRowIndexes.includes(index) && isBlank(row.labelEn)}
                  onChange={function update(changeEvent) {
                    updateRow(index, { labelEn: changeEvent.target.value });
                  }}
                />
                <Input
                  value={row.labelPl}
                  hasError={invalidRowIndexes.includes(index) && isBlank(row.labelPl)}
                  onChange={function update(changeEvent) {
                    updateRow(index, { labelPl: changeEvent.target.value });
                  }}
                />
                <Button
                  variant="destructiveGhost"
                  size="icon"
                  aria-label={t('settings.removeRow')}
                  onClick={function remove() {
                    removeRow(index);
                  }}
                  className="self-start sm:self-auto w-8 h-8"
                >
                  <Icon name="Trash" />
                </Button>
              </div>
            );
          })}
        </div>
      )}

      {invalidRowIndexes.length > 0 ? (
        <FieldError messageKey="patients:settings.incompleteRow" />
      ) : null}

      <div className="flex items-center justify-between mt-3">
        <Button type="button" variant="outline" size="small" disabled={isSaving} onClick={addRow}>
          <Icon name="Plus" /> {t('settings.addRow')}
        </Button>
        <Button type="button" isLoading={isSaving} onClick={save}>
          {t('settings.save')}
        </Button>
      </div>
    </Card>
  );
}

function toRows(items: PatientOptionItem[]): EditableRow[] {
  return items.map(function toRow(item) {
    return {
      code: item.code,
      labelEn: item.labelEn,
      labelPl: item.labelPl,
      imageUrl: item.imageUrl,
      pendingImageFile: null,
      pendingImageRemoved: false,
    };
  });
}
